package org.example.project.screens_cus

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import kotlinx.coroutines.launch
import org.example.project.dashboard.PopupEngineCard
import org.example.project.engineSnap.SnapOwnKeyDialog
import org.example.project.engineSnap.SnapSendDialog
import org.example.project.enginetabular.TabularScreen
import org.example.project.enginetabular.TabularToolbarButton
import org.example.project.enginekartei.KarteiApi
import org.example.project.registrierung.RegistrierungApi
import org.example.project.service.GlobalManager
import org.example.project.service.LoginService
import org.example.project.service.SnapApi
import org.example.project.utils.All
import org.example.project.utils.copyTextToClipboard
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

object SnapverwaltungCreateDialogOverlay {
    var content by mutableStateOf<(@Composable () -> Unit)?>(null)
        private set

    fun show(dialogContent: @Composable () -> Unit) {
        content = dialogContent
    }

    fun close() {
        content = null
    }
}

@Composable
fun SnapverwaltungCreateDialogOverlayLayer() {
    SnapverwaltungCreateDialogOverlay.content?.invoke()
}

private object MandantenDialogStyles : StyleSheet() {
    val title by style {
        fontSize(18.px)
        fontWeight("700")
        color(Color.white)
        property("text-align", "center")
        width(100.percent)
    }

    val listSection by style {
        flex(1)
        minHeight(0.px)
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
    }

    val listFrame by style {
        flex(1)
        minHeight(0.px)
        position(Position.Relative)
    }

    val emptyState by style {
        position(Position.Absolute)
        property("inset", "110px 0 0")
        display(DisplayStyle.Flex)
        justifyContent(JustifyContent.Center)
        alignItems(AlignItems.Center)
        color(Color("rgba(255,255,255,0.60)"))
        fontSize(13.px)
        property("pointer-events", "none")
        property("z-index", "2")
    }
}

private fun nextMandantenRowId(mandanten: List<All>): String =
    ((mandanten.maxOfOrNull { it.id.toIntOrNull() ?: 0 } ?: 0) + 1).toString()

private val snapverwaltungAllHeaders = listOf(
    "Firmen-ID",
    "Rolle",
    "Firma",
    "Straße",
    "Hausnummer",
    "PLZ",
    "Ort",
    "Land",
    "Firma im Netzwerk",
    "Snap Zustand",
    "Kontakte teilen",
    "Zeitfenster teilen",
    "Hof freigeben",
    "Aufträge teilen"
)

private fun mandantenCell(entry: All, header: String): String =
    when (header) {
        "Firmen-ID" -> entry.firmenId
        "Rolle" -> entry.position
        "Firma" -> entry.firma
        "Straße" -> entry.strasse
        "Hausnummer" -> entry.hausnummer
        "PLZ" -> entry.plz
        "Ort" -> entry.ort
        "Land" -> entry.land
        "Firma im Netzwerk" -> if (entry.firmaImNetzwerk) "Ja" else "Nein"
        "Snap Zustand" -> snapStatusText(entry.snapStatus)
        "Kontakte teilen" -> if (entry.snapStatus == "verbunden") snapCheckboxText(entry.snapKontaktlisteTeilen) else ""
        "Zeitfenster teilen" -> if (entry.snapStatus == "verbunden") snapCheckboxText(entry.snapZeitfensterBuchen) else ""
        "Hof freigeben" -> if (entry.snapStatus == "verbunden") snapCheckboxText(entry.snapHofFreigabe) else ""
        "Aufträge teilen" -> if (entry.snapStatus == "verbunden") snapCheckboxText(entry.snapAuftraegeTeilen) else ""
        else -> ""
    }

private fun mandantenRow(entry: All, headers: List<String> = snapverwaltungAllHeaders): List<String> =
    headers.map { mandantenCell(entry, it) }

private fun mandantenFromRow(rowId: String, cols: List<String>, existing: All?): All =
    (existing ?: All(id = rowId)).copy(
        id = rowId,
        firmenId = cols.getOrElse(0) { existing?.firmenId.orEmpty() },
        position = cols.getOrElse(1) { existing?.position.orEmpty() },
        firma = cols.getOrElse(2) { existing?.firma.orEmpty() },
        strasse = cols.getOrElse(3) { existing?.strasse.orEmpty() },
        hausnummer = cols.getOrElse(4) { existing?.hausnummer.orEmpty() },
        plz = cols.getOrElse(5) { existing?.plz.orEmpty() },
        ort = cols.getOrElse(6) { existing?.ort.orEmpty() },
        land = cols.getOrElse(7) { existing?.land.orEmpty() }.ifBlank { "DE" }
    )

private fun mandantenStammdatenChanged(existing: All?, cols: List<String>): Boolean {
    if (existing == null) return true
    return mandantenRow(existing).take(8) != cols.take(8)
}

private fun snapCheckboxText(value: Boolean): String = if (value) "\u2705" else "\u2B1C"

private fun snapCheckboxValue(value: String): Boolean =
    value == "\u2705" || value.equals("true", ignoreCase = true) || value == "1"

private fun snapStatusText(value: String): String =
    when (value.trim().lowercase()) {
        "snap_hinterlegt" -> "Snap hinterlegt"
        "snap_abgelehnt" -> "Snap abgelehnt"
        "verbunden" -> "Verbunden"
        "anfrage_gesendet" -> "Anfrage gesendet"
        else -> value.ifBlank { "Snap hinterlegt" }
    }

private fun isRegisterCompanyId(value: String): Boolean =
    value.trim().matches(Regex("^ls\\d+$", RegexOption.IGNORE_CASE))

private fun preferredLinkedFirmenId(entry: All): String =
    listOf(entry.firmenId, entry.lsId, entry.id)
        .map { it.trim() }
        .firstOrNull(::isRegisterCompanyId)
        .orEmpty()

private fun firmenstammPriority(entry: All): Int =
    when {
        entry.art.equals("inhaber", ignoreCase = true) -> 0
        entry.firmenstammPfad.replace("\\", "/").lowercase().endsWith("/firma.json") -> 1
        isRegisterCompanyId(entry.firmenId) || isRegisterCompanyId(entry.lsId) || isRegisterCompanyId(entry.id) -> 2
        else -> 3
    }

private fun snapPartnerKey(entry: All): String =
    preferredLinkedFirmenId(entry).ifBlank {
        listOf(entry.firma.ifBlank { entry.kunde }, entry.plz, entry.strasse, entry.hausnummer, entry.ort, entry.land)
            .joinToString("|") { it.trim().lowercase() }
    }

private fun snapIdentityKeys(entry: All): Set<String> = buildSet {
    preferredLinkedFirmenId(entry)
        .trim()
        .lowercase()
        .takeIf { it.isNotBlank() }
        ?.let { add("id:$it") }
    val addressKey = listOf(
        entry.firma.ifBlank { entry.kunde },
        entry.plz,
        entry.strasse,
        entry.hausnummer,
        entry.ort,
        entry.land
    ).joinToString("|") { it.trim().lowercase() }
    if (addressKey.replace("|", "").isNotBlank()) add("address:$addressKey")
}

@Composable
fun CUSSnapverwaltung() {
    Style(MandantenDialogStyles)
    val mandantVersion = GlobalManager.jsonTopicVersion("mandant.json")
    val settingsVersion = GlobalManager.jsonTopicVersion("settings.json")
    val snapRequestVersion = GlobalManager.jsonTopicVersion("request.json")
    val snapVersion = GlobalManager.jsonDomainVersion("snap")
    val companyVersion = GlobalManager.jsonDomainVersion("company")
    val firmenstammVersion = GlobalManager.jsonDomainVersion("firmenstamm")
    val sessionVersion = GlobalManager.sessionConfigVersion()
    val currentUserId = LoginService.getId().trim()
    val currentFirmenId = LoginService.getUser()?.firmenId.orEmpty().trim()
        .ifBlank { GlobalManager.currentFirmenId.trim() }

    var mandanten by remember { mutableStateOf<List<All>>(emptyList()) }
    var knownMandanten by remember { mutableStateOf<List<All>>(emptyList()) }
    var firmenstammEntries by remember { mutableStateOf<List<All>>(emptyList()) }
    var registeredFirmenIds by remember { mutableStateOf<Set<String>>(emptySet()) }
    var ownSnapKey by remember { mutableStateOf("") }
    var snapKeyDialogOpen by remember { mutableStateOf(false) }
    var invitationKey by remember { mutableStateOf("") }
    var sendDialogOpen by remember { mutableStateOf(false) }
    var sendBusy by remember { mutableStateOf(false) }
    var sendError by remember { mutableStateOf("") }
    var createDialogOpen by remember { mutableStateOf(false) }
    var createBusy by remember { mutableStateOf(false) }
    var createError by remember { mutableStateOf("") }
    var firmenstammSearchStarted by remember { mutableStateOf(false) }
    var firmenstammLetzteFilter by remember { mutableStateOf<Map<String, String>?>(null) }
    var currentFirma by remember { mutableStateOf<All?>(null) }
    var letzteFilter by remember { mutableStateOf<Map<String, String>?>(null) }
    val scope = rememberCoroutineScope()

    suspend fun refreshKnownMandanten() {
        knownMandanten = runCatching { KarteiApi.loadMandanten(currentFirmenId) }.getOrDefault(emptyList())
    }

    suspend fun reload(clearFirmenstamm: Boolean = true) {
        val snapPartners = letzteFilter?.let { runCatching { SnapApi.searchPartners(it) }.getOrDefault(emptyList()) }.orEmpty()
        val rawMandanten = letzteFilter?.let { runCatching { KarteiApi.searchMandanten(it) }.getOrDefault(emptyList()) }.orEmpty()
        mandanten = (snapPartners + rawMandanten)
            .distinctBy { snapPartnerKey(it) }
        val registerCompanies = runCatching { RegistrierungApi.loadCompanies() }.getOrDefault(emptyList())
        registeredFirmenIds = registerCompanies
            .mapNotNull { preferredLinkedFirmenId(it).lowercase().takeIf(String::isNotBlank) }
            .toSet()
        currentFirma = registerCompanies.firstOrNull { it.firmenId == currentFirmenId || it.id == currentFirmenId }
        ownSnapKey = currentFirma?.snapKey.orEmpty()
        if (clearFirmenstamm) firmenstammEntries = emptyList()
    }

    LaunchedEffect(
        currentUserId,
        currentFirmenId,
        mandantVersion,
        settingsVersion,
        snapRequestVersion,
        snapVersion,
        companyVersion,
        firmenstammVersion,
        sessionVersion
    ) {
        reload()
    }

    val hideYardAndOrderShare = currentFirma?.istnetzwerk == true && currentFirma?.istkunde != true
    val headers = if (hideYardAndOrderShare) {
        snapverwaltungAllHeaders.filterNot { it == "Hof freigeben" || it == "Aufträge teilen" }
    } else {
        snapverwaltungAllHeaders
    }
    val shareHeaders = setOf("Kontakte teilen", "Zeitfenster teilen", "Hof freigeben", "Aufträge teilen")
        .filter { it in headers }
        .toSet()

    fun appendMandantRow(prefill: All): All {
        val partnerKey = snapPartnerKey(prefill)
        val existing = mandanten.firstOrNull { snapPartnerKey(it) == partnerKey && partnerKey.isNotBlank() }
        if (existing != null) return existing
        val newId = nextMandantenRowId(mandanten)
        val draft = prefill.copy(id = newId, land = prefill.land.ifBlank { "DE" })
        mandanten = (mandanten + draft).sortedBy { it.id.toIntOrNull() ?: 0 }
        return draft
    }

    TabularScreen(
        screenKey = "Snapverwaltung",
        rollenKey = "Snapverwaltung",
        title = "Snapverwaltung",
        headers = headers,
        models = mandanten,
        data = mandanten.map { entry -> entry.id to mandantenRow(entry, headers) },
        editable = true,
        commitChangedRowsOnly = true,
        useUserSettings = true,
        nonEditableCols = setOf("Rolle", "Firma im Netzwerk", "Snap Zustand"),
        toggleColumns = shareHeaders,
        snapButtonLabel = "Snap hinzufügen",
        allowDelete = true,
        onDeleteRows = { ids ->
            ids.forEach { rowId ->
                val existing = mandanten.firstOrNull { it.id == rowId }
                if (existing?.firmenId?.isNotBlank() == true) {
                    SnapApi.deletePartner(
                        userId = currentUserId,
                        partnerFirmenId = existing.firmenId,
                        firmenId = currentFirmenId
                    )
                } else {
                    KarteiApi.deleteMandanten(listOf(rowId), currentFirmenId)
                }
            }
            reload()
        },
        extraNavButtons = listOf(
            TabularToolbarButton(
                label = "SnapKey",
                icon = "images/networknode.svg",
                enabled = ownSnapKey.isNotBlank(),
                onClick = { snapKeyDialogOpen = true }
            ),
            TabularToolbarButton(
                label = "Snap-Invite",
                icon = "images/networknode.svg",
                onClick = {
                    scope.launch {
                        val response = SnapApi.createRegistrationInvitation(currentUserId, currentFirmenId)
                        val key = response?.einladungsKey.orEmpty()
                        if (response?.ok == true && key.isNotBlank()) {
                            invitationKey = key
                        }
                    }
                }
            )
        ),
        onCreateAction = {
            firmenstammEntries = emptyList()
            firmenstammSearchStarted = false
            firmenstammLetzteFilter = null
            createBusy = false
            createError = ""
            createDialogOpen = true
            scope.launch { refreshKnownMandanten() }
        },
        onSnapAction = {
            sendError = ""
            sendDialogOpen = true
        },
        onRefresh = {
            scope.launch { reload() }
        },
        onSearchWithFilters = { filters ->
            scope.launch {
                letzteFilter = filters
                val snapPartners = runCatching { SnapApi.searchPartners(filters) }.getOrDefault(emptyList())
                val ownMandanten = runCatching { KarteiApi.searchMandanten(filters) }.getOrDefault(emptyList())
                mandanten = (snapPartners + ownMandanten).distinctBy { snapPartnerKey(it) }
            }
        },
        onCellEditTransform = { _, header, _, row ->
            if (header in shareHeaders) {
                val status = row.getOrNull(9).orEmpty()
                if (status != "Verbunden") row else row
            } else {
                row
            }
        },
        onCommit = { rows ->
            scope.launch {
                rows.forEach { (rowId, cols) ->
                    val colsByHeader = headers
                        .mapIndexed { index, header -> header to cols.getOrNull(index).orEmpty() }
                        .toMap()
                    val partnerFirmenId = colsByHeader["Firmen-ID"].orEmpty()
                    val existing = mandanten.firstOrNull {
                        partnerFirmenId.isNotBlank() && it.firmenId == partnerFirmenId
                    } ?: mandanten.firstOrNull { it.id == rowId }
                    if (mandantenStammdatenChanged(existing, cols)) {
                        val toSave = mandantenFromRow(rowId, cols, existing)
                        KarteiApi.saveMandant(toSave.copy(land = toSave.land.ifBlank { "DE" }), currentFirmenId)
                    }
                    if (existing?.snapStatus == "verbunden" && existing.firmenId.isNotBlank()) {
                        SnapApi.savePartnerSettings(
                            userId = currentUserId,
                            partnerFirmenId = existing.firmenId,
                            snapKontaktlisteTeilen = colsByHeader["Kontakte teilen"]?.let(::snapCheckboxValue) ?: existing.snapKontaktlisteTeilen,
                            snapZeitfensterBuchen = colsByHeader["Zeitfenster teilen"]?.let(::snapCheckboxValue) ?: existing.snapZeitfensterBuchen,
                            snapHofFreigabe = colsByHeader["Hof freigeben"]?.let(::snapCheckboxValue) ?: existing.snapHofFreigabe,
                            snapAuftraegeTeilen = colsByHeader["Aufträge teilen"]?.let(::snapCheckboxValue) ?: existing.snapAuftraegeTeilen,
                            firmenId = currentFirmenId
                        )
                    }
                }
                reload()
            }
        }
    )

    if (snapKeyDialogOpen) {
        SnapOwnKeyDialog(
            snapKey = ownSnapKey,
            onClose = { snapKeyDialogOpen = false },
            onCopy = {
                if (ownSnapKey.isNotBlank()) {
                    copyTextToClipboard(ownSnapKey)
                }
            }
        )
    }

    if (invitationKey.isNotBlank()) {
        SnapOwnKeyDialog(
            snapKey = invitationKey,
            title = "SNAP-Invite-Key",
            onClose = { invitationKey = "" },
            onCopy = { copyTextToClipboard(invitationKey) }
        )
    }

    if (sendDialogOpen) {
        SnapSendDialog(
            loading = sendBusy,
            error = sendError,
            onClose = {
                if (!sendBusy) {
                    sendDialogOpen = false
                    sendError = ""
                }
            },
            onSend = { snapKey ->
                sendBusy = true
                sendError = ""
                scope.launch {
                    val preview = SnapApi.previewBySnapKey(
                        userId = currentUserId,
                        snapKey = snapKey,
                        firmenId = currentFirmenId
                    )
                    if (preview == null) {
                        sendBusy = false
                        sendError = "SnapKey nicht gefunden."
                        return@launch
                    }
                    val response = SnapApi.connectBySnapKey(
                        userId = currentUserId,
                        snapKey = snapKey,
                        firmenId = currentFirmenId
                    )
                    sendBusy = false
                    when {
                        response?.ok == true -> {
                            sendDialogOpen = false
                            sendError = ""
                            reload()
                        }
                        response?.error == "snap_key_self" -> sendError = "Du kannst deinen eigenen SnapKey nicht verwenden."
                        response?.error == "snap_key_not_found" -> sendError = "SnapKey nicht gefunden."
                        else -> sendError = "Firma konnte nicht hinzugefügt werden."
                    }
                }
            }
        )
    }

    DisposableEffect(createDialogOpen, createBusy, firmenstammEntries, mandanten, knownMandanten) {
        if (createDialogOpen) {
            SnapverwaltungCreateDialogOverlay.show {
                val existingIdentityKeys = (knownMandanten + mandanten)
                    .flatMap(::snapIdentityKeys)
                    .toSet()
                MandantenFirmenstammDialog(
                    entries = firmenstammEntries.filter { candidate ->
                        val candidateFirmenId = preferredLinkedFirmenId(candidate).lowercase()
                        val candidateIdentityKeys = snapIdentityKeys(candidate)
                        candidateFirmenId in registeredFirmenIds &&
                            candidateFirmenId != "ls1000" &&
                            candidateFirmenId != currentFirmenId.lowercase() &&
                            candidateIdentityKeys.none { it in existingIdentityKeys }
                    },
                    searchStarted = firmenstammSearchStarted,
                    saving = createBusy,
                    error = createError,
                    onSearch = { filters ->
                        firmenstammSearchStarted = true
                        firmenstammLetzteFilter = filters
                        scope.launch {
                            refreshKnownMandanten()
                            firmenstammEntries = runCatching {
                                FirmenstammApi.searchEntries(filters)
                            }.getOrDefault(emptyList())
                        }
                    },
                    onCreate = {
                        if (createBusy) return@MandantenFirmenstammDialog
                        createBusy = true
                        createError = ""
                        scope.launch {
                            val partnerFirmenId = preferredLinkedFirmenId(it)
                            if (partnerFirmenId.isBlank()) {
                                createError = "Für diese Firma fehlt eine gültige Firmen-ID."
                                createBusy = false
                                return@launch
                            }
                            val operationSucceeded = run {
                                val response = SnapApi.requestPartner(
                                    userId = currentUserId,
                                    partnerFirmenId = partnerFirmenId,
                                    firmenId = currentFirmenId
                                )
                                if (response?.ok != true) {
                                    createError = when (response?.error.orEmpty()) {
                                        "snap_user_not_allowed" -> "Keine Berechtigung für Snapverwaltung."
                                        "snap_company_missing" -> "Die Firma wurde im Register nicht gefunden."
                                        "snap_key_self" -> "Die eigene Firma kann nicht angefragt werden."
                                        "snap_mandant_save_failed" -> "Die Firma konnte nicht gespeichert werden."
                                        "snap_request_create_failed" -> "Die SNAP-Anfrage konnte nicht zugestellt werden."
                                        else -> "Die SNAP-Anfrage konnte nicht erstellt werden."
                                    }
                                    false
                                } else {
                                    true
                                }
                            }
                            if (!operationSucceeded) {
                                if (createError.isBlank()) createError = "Die Firma konnte nicht hinterlegt werden."
                                createBusy = false
                                return@launch
                            }
                            letzteFilter = emptyMap()
                            reload(clearFirmenstamm = false)
                            refreshKnownMandanten()
                            firmenstammEntries = firmenstammLetzteFilter?.let { filters ->
                                runCatching { FirmenstammApi.searchEntries(filters) }.getOrDefault(emptyList())
                            }.orEmpty()
                            createBusy = false
                        }
                    },
                    onCreateBlank = {
                        appendMandantRow(All(land = "DE"))
                        createDialogOpen = false
                    },
                    onClose = { createDialogOpen = false }
                )
            }
        } else {
            SnapverwaltungCreateDialogOverlay.close()
        }
        onDispose {
            SnapverwaltungCreateDialogOverlay.close()
        }
    }
}

@Composable
private fun MandantenFirmenstammDialog(
    entries: List<All>,
    searchStarted: Boolean,
    saving: Boolean,
    error: String,
    onSearch: (Map<String, String>) -> Unit,
    onCreate: (All) -> Unit,
    onCreateBlank: () -> Unit,
    onClose: () -> Unit
) {
    val deduplicatedEntries = remember(entries) {
        entries
            .filter {
                it.firmenstammPfad.isNotBlank() || it.firma.isNotBlank() || it.kunde.isNotBlank()
            }
            .sortedWith(
                compareBy<All> { firmenstammPriority(it) }
                    .thenBy { it.firma.ifBlank { it.kunde }.trim().lowercase() }
                    .thenBy { it.plz.trim() }
                    .thenBy { it.strasse.trim().lowercase() }
                    .thenBy { it.hausnummer.trim().lowercase() }
            )
            .distinctBy { entry ->
                listOf(
                    entry.firma.ifBlank { entry.kunde },
                    entry.plz,
                    entry.strasse,
                    entry.hausnummer,
                    entry.ort,
                    entry.land
                ).joinToString("|") { value -> value.trim().lowercase() }
            }
    }

    val tabularRows = remember(deduplicatedEntries) {
        deduplicatedEntries.mapIndexed { index, entry ->
            val rowId = entry.firmenstammPfad.ifBlank {
                buildString {
                    append(entry.firma.ifBlank { entry.kunde }.trim())
                    append("|")
                    append(entry.plz.trim())
                    append("|")
                    append(entry.strasse.trim())
                    append("|")
                    append(entry.hausnummer.trim())
                    append("|")
                    append(index)
                }
            }
            rowId to listOf(
                entry.firma.ifBlank { entry.kunde },
                entry.plz,
                entry.strasse,
                entry.hausnummer
            )
        }
    }
    val entryByRowId = remember(deduplicatedEntries, tabularRows) {
        tabularRows.mapIndexed { index, (rowId, _) -> rowId to deduplicatedEntries[index] }.toMap()
    }
    var selectedValue by remember(tabularRows) { mutableStateOf(tabularRows.firstOrNull()?.first.orEmpty()) }
    val selectedEntry = entryByRowId[selectedValue]

    PopupEngineCard(
        windowKey = "mandanten-firmenstamm-auswahl",
        widthPx = 820,
        heightPx = 560,
        minWidthPx = 720,
        minHeightPx = 460,
        viewportAnchored = true,
        onClose = onClose
    ) {
        Div({
            style {
                width(100.percent)
                height(100.percent)
                property("padding", "44px 20px 20px")
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(14.px)
                boxSizing("border-box")
            }
        }) {
            Span({ classes(MandantenDialogStyles.title) }) { Text("Bestehende Firma übernehmen oder neu anlegen") }

            if (error.isNotBlank()) {
                Div({
                    style {
                        color(Color("#ff6b6b"))
                        fontSize(12.px)
                        fontWeight("600")
                    }
                }) { Text(error) }
            }

            Div({ classes(MandantenDialogStyles.listSection) }) {
                Div({ classes(MandantenDialogStyles.listFrame) }) {
                    TabularScreen(
                            screenKey = "",
                            title = null,
                            headers = listOf("Firma", "PLZ", "Straße", "Hausnummer"),
                            data = tabularRows,
                            models = emptyList(),
                            editable = false,
                            useUserSettings = false,
                            createButtonLabel = "Erstellen",
                            onCreateAction = onCreateBlank,
                            allowSave = false,
                            allowDelete = false,
                            allowImportButton = false,
                            allowRefreshButton = false,
                            allowExportButton = false,
                            allowSettingsButton = false,
                            nonEditableCols = setOf("Firma", "PLZ", "Straße", "Hausnummer"),
                            includeMetadata = false,
                            selectedRowIndices = emptySet(),
                            onRowClick = { rowId -> selectedValue = rowId },
                            extraNavButtons = listOf(
                                TabularToolbarButton(
                                    label = "Übernehmen",
                                    icon = "images/joinnetwork.svg",
                                    enabled = selectedEntry != null && !saving,
                                    onClick = {
                                        selectedEntry?.let { entry ->
                                            onCreate(
                                                All(
                                                    firmenId = preferredLinkedFirmenId(entry),
                                                    position = entry.position,
                                                    firma = entry.firma.ifBlank { entry.kunde },
                                                    strasse = entry.strasse,
                                                    hausnummer = entry.hausnummer,
                                                    plz = entry.plz,
                                                    ort = entry.ort,
                                                    land = entry.land.ifBlank { "DE" }.uppercase()
                                                )
                                            )
                                        }
                                    }
                                )
                            ),
                            smartMode = true,
                            onSearchWithFilters = onSearch,
                            onCommit = { }
                    )
                    if (!searchStarted) {
                        Div({ classes(MandantenDialogStyles.emptyState) }) {
                            Text("Firmen werden erst nach dem Suchen angezeigt.")
                        }
                    }
                }
            }
        }
    }
}
